import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import * as css from './CaseStudy.m.css';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { Image } from '../../widgets/Image';
import { Section } from '../../widgets/Section';
import { Technologies } from '../../widgets/Technologies';
import { Text } from '../../widgets/Text';
import ContentWrapper from '../../widgets/ContentWrapper';

const trowePriceCardLeft = require('../../assets/images/work-mockups/t-rowe-card-bottom-right.png');
const trowePriceCardLeftHighRes = require('../../assets/images/work-mockups/t-rowe-card-bottom-right@2x.png');
const trowePriceCardRight = require('../../assets/images/work-mockups/t-rowe-card-bottom-left.png');
const trowePriceCardRightHighRes = require('../../assets/images/work-mockups/t-rowe-card-bottom-left@2x.png');
const trowePriceMockup = require('../../assets/images/work-mockups/t-rowe-mockup.png');
const trowePriceMockupHighRes = require('../../assets/images/work-mockups/t-rowe-mockup@2x.png');

const factory = create();

export const CaseStudyTRowePrice = factory(function CaseStudyTRowePrice() {
	return (
		<div classes={css.root}>
			<Head
				title="System Performance and Delivery Model Analysis | SitePen"
				description="Learn how SitePen analyzed application performance issues and improved the operating model of the institutional investment technology team within a major financial institution."
			/>

			<Hero background="level1PurpleTeal" align="center" blurbWidth="md" paddingBottom="sm">
				{{
					title: 'Case Study',
					xlTitle: 'T. Rowe Price',
					blurb: (
						<virtual>
							Breaking down product silos, aligning technology stacks, standardizing
							delivery pipelines, sharing enterprise design libraries, addressing
							system performance issues, all while undertaking a major product upgrade
							is a daunting prospect. How can a division navigate such challenges and
							successfully deliver on their goals? A financial enterprise looked to
							SitePen for expert guidance in technology and operating model
							recommendations to accomplish just that.
							<Technologies
								icons={['javascript', 'angular', 'react', 'java', 'adobe']}
							/>
						</virtual>
					)
				}}
			</Hero>

			<Section type="leading" background>
				<ContentWrapper size="small">
					<Text type="section">The Situation</Text>
					<Text type="body">
						The institutional investment division of a major financial organization was
						experiencing issues with its fund information product portal. Slow
						performance accessing critical fund data resulted in reports of user
						dissatisfaction and reduced engagement metrics. The historical layering
						within the technology group’s operational structure further hampered the
						ability to promptly identify and resolve the issues. The division comprised
						several main product areas brought together through multiple acquisitions
						over fifteen-plus years, each with a separate history of development
						methodologies, technology choices, and culture.
					</Text>
					<Text type="body">
						While the team had made strides in aligning and encouraging sharing across
						all groups, many still felt hampered in their ability to manage and deliver
						on their product portfolio. Fundamental improvements were needed if the team
						were to meet their ongoing business requirements and improve their users’
						experience. The technology leadership team looked to SitePen as a neutral
						third party to provide unbiased analysis of the performance and operational
						problems, propose solutions for the issues identified, and guide the team on
						the reasoning behind the answers to aid buy-in as to why each area needed
						fixing.
					</Text>
				</ContentWrapper>
			</Section>

			<Section shaded type="leading" background>
				<ContentWrapper size="small">
					<div classes={[css.topImageContainer]}>
						<img
							loading="lazy"
							classes={[css.topLeftImage]}
							src={trowePriceCardLeft}
							srcset={`${trowePriceCardLeftHighRes} 2x`}
							alt=""
						/>
						<img
							loading="lazy"
							classes={[css.topRightImage]}
							src={trowePriceCardRight}
							srcset={`${trowePriceCardRightHighRes} 2x`}
							alt=""
						/>
					</div>
					<div classes={css.mainImageContainer}>
						<Image
							src={trowePriceMockup}
							highResSrc={trowePriceMockupHighRes}
							align="center"
							lazy
						/>
					</div>
					<Text type="section">The Solution</Text>
					<Text type="body">
						SitePen’s engagement involved two work streams; one to analyze the
						investment web application, focusing on performance, and another to
						understand the technology group’s current operating model and the challenges
						the team faced with it.
					</Text>
					<Text type="smaller-subhead">Performance</Text>
					<Text type="body">
						The website analysis stream targeted areas of performance and accessibility,
						with improvement suggestions extending to overall architecture, technology
						choice, general quality upliftment, and engineering best practices. The
						investment portal relied on a third-party content platform which remained
						invariable in the short term given the significant time and investment
						needed to replace it. Despite this restriction, SitePen identified many
						enhancements in the entire web application structure and how the client
						could better utilize the content platform to improve the user experience
						when accessing fund details.
					</Text>
					<Text type="smaller-subhead">Operating Model</Text>
					<Text type="body">
						The operating model stream first interviewed a wide range of critical
						stakeholders within the technology, product, and management teams to
						understand the ownership structure and product delivery pipelines across the
						group’s verticals. Information gathered from these discussions was collated,
						summarized, and delivered to the client as a documented record of the
						current operating state. This record included the main issues the team faced
						and the impact each had on the ability to design and deliver features within
						their platform.
					</Text>
					<Text type="body">
						Once SitePen had gathered a thorough understanding, including all
						significant friction points, they prepared a new target operating model. The
						main goals of the new model were bringing end-user value delivery to the
						forefront while also addressing many of the current impediments faced within
						the product and technology delivery pipelines. A set of targeted
						recommendations, grouped into pillar areas of strategy, operations, and
						technology, formed the basis for the client to implement the new operating
						model and realize its benefits.
					</Text>
				</ContentWrapper>
			</Section>

			<Section>
				<ContentWrapper size="small">
					<Text type="section">The Result</Text>
					<Text type="body">
						The fixed-scope nature of the project meant SitePen’s focus was analyzing
						the current state and providing future state improvement recommendations,
						with the client handling further implementation activities. SitePen
						undertook a targeted performance and usability analysis of the investment
						portal and an in-depth assessment of the division's current operating model
						to manage its product suite.
					</Text>
					<Text type="body">
						SitePen delivered comprehensive recommendations and a new operating model
						targeting all identified areas. Review walkthroughs were then conducted with
						the engineering and management teams to ensure everyone fully understood the
						issues identified and the options available for improving each point. The
						outcomes were well-received by everyone involved, and the team was left
						enthused by the benefits to come when implementing SitePen’s
						recommendations.
					</Text>
				</ContentWrapper>
			</Section>
			<Section type="trailing" shaded>
				<ConnectStrip trailing />
			</Section>
		</div>
	);
});

export default CaseStudyTRowePrice;
